/**
 * Service worker registration and app updates.
 *
 * vite-plugin-pwa runs in `autoUpdate` mode: a new worker activates as soon as it is
 * installed, and the page should then reload to use the new files. Reloading in the middle
 * of a race would throw the player out, so the reload is only *announced* here
 * (`updateReady`); the app applies it at a safe moment (see createApp).
 */

import { registerSW } from 'virtual:pwa-register';
import { signal } from '@preact/signals';
import type { AppUpdater } from './types';

/** An installed app may stay open for days without navigating: look for updates hourly. */
const UPDATE_CHECK_MS = 60 * 60_000;
/** How long "Обновить" waits for a freshly found worker before reloading anyway. */
const ACTIVATION_TIMEOUT_MS = 8000;

export function createUpdater(): AppUpdater {
  const updateReady = signal(false);
  let registration: ServiceWorkerRegistration | undefined;
  let reloading = false;

  const reload = () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  };

  const checkForUpdate = () => {
    registration?.update().catch(() => {
      // Offline or server down: try again later.
    });
  };

  registerSW({
    immediate: true,
    onNeedReload: () => {
      updateReady.value = true;
    },
    onRegisteredSW: (_url, swRegistration) => {
      registration = swRegistration;
      setInterval(checkForUpdate, UPDATE_CHECK_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      });
    },
  });

  return {
    updateReady,
    applyUpdate: reload,
    async updateAndReload() {
      if (registration) {
        await registration.update().catch(() => undefined);
        // Without this wait the reload could be answered by the old worker's cache.
        if (registration.installing || registration.waiting) {
          await waitForNewWorker(ACTIVATION_TIMEOUT_MS);
        }
      }
      reload();
    },
  };
}

function waitForNewWorker(timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}
